import React, {Component, Fragment} from 'react';
import gql from 'graphql-tag';
import {Query} from 'react-apollo';
import ArticleWrapper from 'presentational/ArticleWrapper/ArticleWrapper';
import ArticleSchema from './ArticleSchema';
import PageMeta from 'presentational/PageMeta/PageMeta';
import AnchorsSection from 'presentational/AnchorsSection/AnchorsSection';
import Breadcrumbs from 'presentational/Breadcrumbs/Breadcrumbs';
import Hero from 'presentational/ArticleHero/ArticleHero';
import SocialShare from 'presentational/SocialShare/SocialShare';
import RichText from 'presentational/RichText/RichText';
import RelatedArticles from 'presentational/RelatedArticles/RelatedArticles';
import ErrorPage from 'presentational/ErrorPage/ErrorPage';
import SeoLinkpile from 'presentational/SeoLinkpile/SeoLinkpile';
import {reach} from 'hoek';

import {getBreadcrumbs, categoryLookup} from 'utils/utils';
import {HomeRouteContext} from 'utils/context';
import withQuery from 'utils/withQuery';

export const query = gql`
    query articlePage($sitename: String, $slug: String, $preview: Boolean) {
        articles: articleCollection(
            where: {site: $sitename, slug: $slug}
            preview: $preview
        ) {
            items {
                slug
                site
                headline
                parentId
                heroFile {
                    url
                }
                heroVideo
                metaTitle
                metaDescription
                datePublishedCustom
                sys {
                    publishedAt
                }
                richTextContent {
                    json
                    links {
                        entries {
                            block {
                                sys {
                                    id
                                }
                                ... on MediaBlock {
                                    caption
                                    alignMedia
                                    mediaFile {
                                        url
                                        description
                                    }
                                }
                                ... on ExternalLinkCarousel {
                                    title
                                    externalLinksCollection {
                                        items {
                                            title
                                            details
                                            link
                                            image {
                                                url
                                            }
                                        }
                                    }
                                }
                            }
                            inline {
                                sys {
                                    id
                                }
                                ... on ArticleLink {
                                    text
                                    uri
                                    relationship
                                }
                            }
                        }
                    }
                }
                hreflangData: hreflang
            }
        }
    }
`;

const LoadedArticle = (props) => {
    const {
        allCategories,
        translations,
        systemData: {hostname, siteName},
        systemData,
        response,
        homeRoute,
        location,
        isMobile,
        socialShares,
        slug,
    } = props;
    const isDevice = isMobile || systemData.isMobile || systemData.isTablet;
    const article = reach(response, 'data.articles.items.0');

    if (!article) {
        return <ErrorPage {...props} />;
    }

    const {
        richTextContent,
        headline,
        heroFile,
        heroVideo,
        parentId,
        metaDescription,
    } = article;

    const parentCategory = categoryLookup(allCategories, 'sys.id', parentId);
    const crumbs = getBreadcrumbs(
        allCategories,
        {name: translations.home, url: homeRoute},
        parentCategory,
        article,
    );
    const canonical = `https://${hostname + homeRoute + parentCategory.path}/${
        article.slug
    }`;
    const sections = richTextContent.json.content.filter((type) => {
        return type.nodeType === 'heading-2' || type.nodeType === 'heading-3';
    });

    const seoLinkpilePath = `${homeRoute}${parentCategory.path}/${
        article.slug
    }`;

    const linkpileProps = {
        site: siteName,
        canonicalLink: seoLinkpilePath,
        isMobile: isMobile || systemData.isMobile,
    };

    return (
        <Fragment>
            <ArticleWrapper>
                <meta itemProp="mainEntityOfPage" content={canonical} />
                <PageMeta
                    {...article}
                    {...{systemData}}
                    canonical={canonical}
                    isArticle="true"
                />
                <ArticleSchema
                    headline={headline}
                    {...article}
                    {...{systemData}}
                    canonical={canonical}
                />
                <Hero
                    title={headline}
                    image={heroFile}
                    video={heroVideo}
                    renderedCrumbs={
                        <Breadcrumbs
                            crumbs={crumbs}
                            hostname={systemData.hostname}
                        />
                    }
                />
                <meta itemProp="description" content={metaDescription} />
                <SocialShare
                    socialShares={socialShares}
                    copiedText={translations.copyLink}
                />
                <AnchorsSection
                    sections={sections}
                    currentHash={location.hash}
                />
                <RichText {...{richTextContent, translations, isDevice}} />
                <RelatedArticles
                    title={translations.exploreArticles}
                    category={parentId}
                    slug={`/${slug}`}
                />
            </ArticleWrapper>
            <SeoLinkpile {...linkpileProps} />
        </Fragment>
    );
};

const ArticleWithLoading = withQuery(LoadedArticle);

export default class ArticleContainer extends Component {
    static contextType = HomeRouteContext;
    render() {
        const {slug, systemData, preview} = this.props;
        const articleSlug = slug.split('/').pop();
        const homeRoute = this.context;
        const variables = {
            sitename: systemData.siteName,
            slug: articleSlug,
            preview,
        };

        return (
            <Query {...{query, variables}}>
                {(response) => {
                    return (
                        <ArticleWithLoading
                            {...{...this.props, response, homeRoute}}
                        />
                    );
                }}
            </Query>
        );
    }
}
